import {useState, useEffect, useMemo} from 'react'
import {calculateWorkingDays} from '@/features/projects/utils'
import {useGetHolidays} from '@/features/projects/queries/useGetHolidays'
import dayjs from 'dayjs'

interface UseWorkingDaysProps {
    startDate: Date | null
    expirationDate: Date | null
    durationDays?: number | '' // Optional duration in working days
}

/**
 * Calculate the number of working days between two dates
 * It uses the holidays api to get the holidays for the year of the start date and the expiration date
 * If the start date and the expiration date are in the same year, it will use the duration in working days
 * @param startDate - The start date
 * @param expirationDate - The expiration date
 * @param durationDays - The duration in working days
 * @returns The number of working days
 */
export const useWorkingDays = ({startDate, expirationDate, durationDays}: UseWorkingDaysProps): number => {
    const [workingDays, setWorkingDays] = useState<number>(0)
    const diffYear = dayjs(expirationDate).year() - dayjs(startDate).year()

    const {
        data: startDateHolidays,
        isError: startDateIsError,
        isSuccess: startDateIsSuccess
    } = useGetHolidays({
        urlParams: {year: startDate ? dayjs(startDate).year() : new Date().getFullYear()},
        options: {
            enabled: !!startDate && diffYear < 2
        }
    })

    const {
        data: expirationDateHolidays,
        isError: expirationDateIsError,
        isSuccess: expirationDateIsSuccess
    } = useGetHolidays({
        urlParams: {year: expirationDate ? dayjs(expirationDate).year() : new Date().getFullYear()},
        options: {
            enabled: diffYear < 2
        }
    })

    const holidays = useMemo(
        () => [...(startDateHolidays ?? []), ...(expirationDateHolidays ?? [])],
        [startDateHolidays, expirationDateHolidays]
    )
    const isError = useMemo(
        () => startDateIsError || expirationDateIsError || diffYear > 1,
        [startDateIsError, expirationDateIsError, diffYear]
    )
    const isSuccess = useMemo(
        () => startDateIsSuccess && expirationDateIsSuccess,
        [startDateIsSuccess, expirationDateIsSuccess]
    )

    useEffect(() => {
        if (isError || holidays.length == 0) {
            if (startDate && expirationDate) {
                const days = calculateWorkingDays(startDate, expirationDate)
                setWorkingDays(days)
            } else if (startDate && durationDays) {
                setWorkingDays(durationDays)
            } else {
                setWorkingDays(0)
            }
        }
    }, [isError, startDate, expirationDate, durationDays, holidays])

    useEffect(() => {
        if (isSuccess && holidays && startDate) {
            const holidaysDates = holidays.map(holiday => dayjs(holiday.date).toDate())
            if (expirationDate) {
                const days = calculateWorkingDays(
                    startDate,
                    expirationDate,
                    holidaysDates?.length > 0 ? holidaysDates : undefined
                )
                setWorkingDays(days)
            } else if (durationDays) {
                setWorkingDays(durationDays)
            } else {
                setWorkingDays(0)
            }
        }
    }, [isSuccess, holidays, startDate, expirationDate, durationDays])

    return workingDays || 0
}
